import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  CircleQuestionMarkIcon,
  Trash2Icon,
  UserPlusIcon,
  XIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { mobileOnly, useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { DateInput } from "simplydo/react/ui/date";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Modal, ModalActions, ModalHeader } from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { UserChooser } from "components/lib/Choosers";
import { ChallengeAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning";
import ProjectComments from "components/lib/ProjectBoard/ProjectComments";

import type {
  IdeaPreviewIdea,
  IdeaPreviewInvitation,
  IdeaPreviewProjectBoard,
  IdeaWithAuthors,
} from "../types";

const DateInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 33%;
  width: 33%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  ${mobileOnly} {
    width: 100%;
    flex: 1 1 100%;
  }
`;

const OpenModalAction = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  span {
    display: block;
    font-style: italic;
    color: gray;
    text-decoration-line: underline;
    cursor: pointer;
  }
`;

const ExistingAssignees = styled.div`
  max-width: 100%;
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
`;

const PendingInvites = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const PendingInvite = styled.div`
  display: flex;
  &:not(:last-of-type) {
    margin-bottom: 20px;
  }
  justify-content: space-between;
  align-items: center;
  span {
    display: block;
    margin-right: 10px;
  }
  div {
    display: flex;
    align-items: center;
  }
`;

type ChooserUser = OpenAPI.Schemas["User"] & { isEmailInvitee?: boolean };

type ProjectManagementProps = {
  idea: IdeaWithAuthors;
  handleUpdateProjectLane: (laneId: string) => void;
  canManage: boolean;
};

export const ProjectManagement = ({
  idea,
  handleUpdateProjectLane,
  canManage,
}: ProjectManagementProps) => {
  const [invitationsModalOpen, setInvitationsModalOpen] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<
    IdeaPreviewInvitation[]
  >([]);
  const [assignmentNote, setAssignmentNote] = useState("");

  const { t } = useTranslation();
  const theme = useTheme();

  const projectLaneQuery = useQuery(
    initUseQuery(api.boards.getProjectLanes, [idea.challenge]),
  );

  const ideaId = idea._id;
  const challengeId = idea.challenge;
  const board = idea?.projectManagement?.boards?.find(
    (b) => b.forId === idea.challenge,
  ) as IdeaPreviewProjectBoard | undefined;
  const user = useCurrentUser();
  const currentUser = user!;

  const userIsAssigned = useMemo(
    () =>
      !!(board?.assignees || []).find(
        (assigneeId: string) => assigneeId === currentUser._id,
      ),
    [currentUser, board],
  );

  const allLanes = useMemo(() => {
    return [
      { _id: "default", text: "Default", value: "default" },
      ...(projectLaneQuery.data?.lanes.map((l) => ({
        _id: l._id,
        value: l._id,
        text: l.name,
      })) || []),
    ];
  }, [projectLaneQuery.data?.lanes]);

  const getInvitations = useCallback(() => {
    api.invitations.getForType(
      "projectInvited",
      ideaId,
      ({ invitations }) => {
        setPendingInvitations(invitations);
      },
      () => {},
    );
  }, [ideaId]);

  useEffect(() => {
    getInvitations();
  }, [getInvitations]);

  const handleSearch = useCallback(
    (
      search: string,
      callback: (users: OpenAPI.Schemas["User"][]) => void,
      fail: (err: { message: string }) => void,
    ) => {
      api.boards.searchProjectAssignees(
        challengeId,
        search,
        ({ users: potentialAssignees }) => {
          callback(potentialAssignees);
        },
        fail,
      );
    },
    [challengeId],
  );

  const addAssignee = useCallback(
    (assigneeId: string, assignmentNote?: string) => {
      api.boards.assignProjectIdea(
        challengeId,
        ideaId,
        assigneeId,
        { message: assignmentNote },
        ({ assignees, assigneeUsers, updatedAt }) => {
          const existingBoard = idea?.projectManagement?.boards?.find(
            (b) => b.forId === idea.challenge,
          );
          if (!existingBoard || !idea.projectManagement?.boards) {
            return;
          }
          const newBoard: IdeaPreviewProjectBoard = {
            ...existingBoard,
            assigneeUsers,
            assignees,
            updatedAt,
          };
          util.onUpdateIdea(
            idea,
            {
              ...idea.projectManagement,
              boards: [
                ...idea.projectManagement.boards.filter(
                  (b) => b.forId !== idea.challenge,
                ),
                newBoard,
              ],
            },
            ["projectManagement"],
          );
          if (assignmentNote) {
            api.invalidateQueries(api.boards.getProjectComments, [
              challengeId,
              ideaId,
            ]);
          }
        },
        (err) => toast.error(err.message),
      );
    },
    [ideaId, challengeId, idea],
  );

  const addAllAssignees = useCallback(
    (usersToAssign: ChooserUser[], assignmentNote?: string) => {
      const emailUsers = usersToAssign.filter((u) => u.isEmailInvitee);
      const existingUsers = usersToAssign.filter((u) => !u.isEmailInvitee);
      if (emailUsers.length > 0) {
        api.invitations.createBulk(
          {
            invitees: emailUsers.map((u) => u._id),
            invitationType: "email",
            forType: "projectInvited",
            forId: ideaId,
            // @ts-expect-error
            board: { forId: challengeId },
          },
          () => {
            getInvitations();
            toast.success(`Invitation${emailUsers.length > 1 ? "s" : ""} sent`);
            if (assignmentNote) {
              api.boards.postProjectComment(
                challengeId,
                ideaId,
                {
                  message: {
                    text: `${currentUser.profile.fullName} invited ${util.pluralise(emailUsers.length, "email user", "email users")} to this card: ${assignmentNote}`,
                  },
                },
                () => {},
                () => {},
              );
            }
          },
          () => {},
        );
      }
      if (existingUsers.length > 0) {
        existingUsers.forEach((u) => {
          addAssignee(u._id, assignmentNote);
        });
      }
    },
    [challengeId, ideaId, currentUser, addAssignee, getInvitations],
  );

  const removeAssignee = useCallback(
    (assigneeId: string) => {
      util
        .confirm(
          "Remove assignee",
          "This user will be un-assigned from this project card, and will be unable to complete any project actions until you re-assign them. Are you sure you want to do this?",
        )
        .then(() => {
          api.boards.unassignProjectIdea(
            challengeId,
            ideaId,
            assigneeId,
            ({ assignees, assigneeUsers, updatedAt }) => {
              if (!idea.projectManagement?.boards) {
                return;
              }
              const existingBoard = idea.projectManagement.boards.find(
                (b) => b.forId === idea.challenge,
              );
              if (!existingBoard) {
                return;
              }
              const newBoard: IdeaPreviewProjectBoard = {
                ...existingBoard,
                assigneeUsers,
                assignees,
                updatedAt,
              };
              util.onUpdateIdea(
                idea,
                {
                  ...idea.projectManagement,
                  boards: [
                    ...idea.projectManagement.boards.filter(
                      (b) => b.forId !== idea.challenge,
                    ),
                    newBoard,
                  ],
                },
                ["projectManagement"],
              );
            },
            (err) => toast.error(err.message),
          );
        })
        .catch(() => {});
    },
    [ideaId, challengeId, idea],
  );

  const updateProjectDeadline = useCallback(
    (inputDate: string | Date | null) => {
      if (!board) return;
      const deadline = inputDate ? new Date(inputDate) : null;
      const currentDeadline = board.deadline ? new Date(board.deadline) : null;
      if (
        (!deadline && !currentDeadline) ||
        deadline?.getTime() === currentDeadline?.getTime()
      ) {
        return;
      }
      api.boards.updateProjectIdeaDeadline(
        challengeId,
        ideaId,
        deadline,
        () => {
          util.onUpdateIdea(
            idea,
            {
              ...idea.projectManagement,
              boards: idea.projectManagement.boards.map((b) =>
                b.forId === idea.challenge
                  ? { ...b, deadline: deadline && new Date(deadline) }
                  : b,
              ),
            },
            ["projectManagement"],
          );
        },
        (err) => toast.error(err.message),
      );
    },
    [ideaId, idea, board, challengeId],
  );

  const addReviewDateNote = useCallback(() => {
    util
      .prompt(
        `Add ${t("generic.ideaWithArticle")} review note`,
        `What would you like to talk about during the next review of this ${t("generic.idea")}? The note will be added to the project notes section.`,
        (reviewDateNote) => {
          if (reviewDateNote) {
            const formattedReviewDateNote = `Note for next review: ${reviewDateNote}`;
            api.boards.postProjectComment(
              challengeId,
              ideaId,
              { message: { text: formattedReviewDateNote } },
              () => {
                api.invalidateQueries(api.boards.getProjectComments, [
                  challengeId,
                  ideaId,
                ]);
              },
              () => {},
            );
          }
        },
      )
      .catch(() => {});
  }, [challengeId, ideaId, t]);

  const updateProjectReviewDate = useCallback(
    (inputDate: string | Date | null) => {
      if (!board) return;
      const reviewDate = inputDate ? new Date(inputDate) : null;
      const currentReviewDate = board.reviewDate
        ? new Date(board.reviewDate)
        : null;
      if (
        (!reviewDate && !currentReviewDate) ||
        reviewDate?.getTime() === currentReviewDate?.getTime()
      ) {
        return;
      }
      api.boards.updateProjectIdeaReviewDate(
        challengeId,
        ideaId,
        reviewDate,
        () => {
          util.onUpdateIdea(
            idea,
            {
              ...idea.projectManagement,
              boards: idea.projectManagement.boards.map((b) =>
                b.forId === idea.challenge
                  ? { ...b, reviewDate: reviewDate && new Date(reviewDate) }
                  : b,
              ),
            },
            ["projectManagement"],
          );
          if (
            reviewDate &&
            (canManage ||
              util.hasPermission(
                currentUser,
                "challenge.createProjectNotes",
                challengeId,
              ))
          )
            addReviewDateNote();
        },
        (err) => toast.error(err.message),
      );
    },
    [
      ideaId,
      idea,
      board,
      challengeId,
      addReviewDateNote,
      currentUser,
      canManage,
    ],
  );

  const removePendingInvitation = useCallback(
    (invitationId: string) => {
      api.invitations.remove(
        invitationId,
        () => {
          const updatedPendingInvitations = pendingInvitations.filter(
            (i) => i._id !== invitationId,
          );
          setPendingInvitations(updatedPendingInvitations);
          toast.success(
            "Invitation removed. The user can no longer accept it.",
          );
        },
        () => toast.error("Failed to remove invite"),
      );
    },
    [pendingInvitations],
  );
  const canViewProjectBoard =
    userIsAssigned ||
    canManage ||
    util.hasPermission(currentUser, "challenge.viewProjectBoard", challengeId);

  return (
    <>
      {canViewProjectBoard ||
      util.hasPermission(
        currentUser,
        "challenge.editProjectDates",
        challengeId,
      ) ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#f7f7f7",
            borderRadius: 5,
            padding: 5,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexDirection: theme.sizes.isMobile ? "column" : "row",
              flexWrap: "wrap",
              flex: 1,
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <DateInputContainer>
              <b>In lane</b>
              <Dropdown
                fluid
                variant="outline"
                disabled={
                  !userIsAssigned &&
                  !canManage &&
                  !util.hasPermission(
                    currentUser,
                    "challenge.moveProjectIdeas",
                    idea.challenge,
                  )
                }
                value={board?.lane || "default"}
                options={allLanes}
                onChange={(_e, { value }) =>
                  handleUpdateProjectLane(String(value ?? ""))
                }
              />
            </DateInputContainer>
            <DateInputContainer>
              <b>Review date</b>
              <DateInput
                fluid
                clearable={
                  userIsAssigned ||
                  canManage ||
                  util.hasPermission(
                    currentUser,
                    "challenge.editProjectDates",
                    idea.challenge,
                  )
                }
                closable={true}
                disabled={
                  !userIsAssigned &&
                  !canManage &&
                  !util.hasPermission(
                    currentUser,
                    "challenge.editProjectDates",
                    idea.challenge,
                  )
                }
                placeholder="Add a review date"
                value={
                  board?.reviewDate
                    ? formatDate(board.reviewDate, "yyyy-MM-dd")
                    : ""
                }
                dateFormat="yyyy-MM-dd"
                iconPosition="left"
                onChange={(
                  _event: React.SyntheticEvent,
                  { value }: { value?: string },
                ) =>
                  updateProjectReviewDate(
                    formatDate(String(value ?? ""), "yyyy-MM-dd"),
                  )
                }
                onClear={() => updateProjectReviewDate(null)}
              />
            </DateInputContainer>
            <DateInputContainer>
              <b>Deadline</b>
              <DateInput
                fluid
                clearable={
                  userIsAssigned ||
                  canManage ||
                  util.hasPermission(
                    currentUser,
                    "challenge.editProjectDates",
                    idea.challenge,
                  )
                }
                disabled={
                  !userIsAssigned &&
                  !canManage &&
                  !util.hasPermission(
                    currentUser,
                    "challenge.editProjectDates",
                    idea.challenge,
                  )
                }
                placeholder="Add a deadline"
                value={
                  board?.deadline
                    ? formatDate(board.deadline, "yyyy-MM-dd")
                    : ""
                }
                dateFormat="yyyy-MM-dd"
                iconPosition="left"
                onChange={(
                  _event: React.SyntheticEvent,
                  { value }: { value?: string },
                ) =>
                  updateProjectDeadline(
                    formatDate(String(value ?? ""), "yyyy-MM-dd"),
                  )
                }
                onClear={() => updateProjectDeadline(null)}
              />
            </DateInputContainer>
          </div>
        </div>
      ) : null}
      <div>
        {(userIsAssigned ||
          canManage ||
          util.hasPermission(
            currentUser,
            "challenge.viewProjectAssignees",
            idea.challenge,
          ) ||
          util.hasPermission(
            currentUser,
            "challenge.editProjectAssignees",
            idea.challenge,
          )) && (
          <div style={{ marginTop: 10 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "#f7f7f7",
                borderRadius: 5,
                padding: 5,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {(userIsAssigned ||
                  canManage ||
                  util.hasPermission(
                    currentUser,
                    "challenge.viewProjectAssignees",
                    idea.challenge,
                  )) &&
                board &&
                board.assignees &&
                board?.assigneeUsers?.length ? (
                  <div>
                    <div className="content-item">
                      <ExistingAssignees>
                        {board.assigneeUsers?.map(
                          (a: OpenAPI.Schemas["User"]) => (
                            <UserChip
                              compact
                              user={a}
                              key={a._id}
                              style={{ marginRight: 15 }}
                              actionIcon={
                                canManage ||
                                util.hasPermission(
                                  currentUser,
                                  "challenge.editProjectAssignees",
                                  idea.challenge,
                                )
                                  ? XIcon
                                  : undefined
                              }
                              actionClick={
                                canManage ||
                                util.hasPermission(
                                  currentUser,
                                  "challenge.editProjectAssignees",
                                  idea.challenge,
                                )
                                  ? () => removeAssignee(a._id)
                                  : undefined
                              }
                            />
                          ),
                        )}
                      </ExistingAssignees>
                    </div>
                  </div>
                ) : null}
                {(userIsAssigned ||
                  canManage ||
                  util.hasPermission(
                    currentUser,
                    "challenge.viewProjectAssignees",
                    idea.challenge,
                  )) &&
                pendingInvitations.length > 0 ? (
                  <OpenModalAction>
                    <span
                      style={{ marginRight: 20 }}
                      onClick={() => setInvitationsModalOpen(true)}
                    >
                      View pending invitations
                    </span>
                  </OpenModalAction>
                ) : null}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <UserChooser
                  trigger={
                    <Button
                      size="small"
                      disabled={
                        !canManage &&
                        !util.hasPermission(
                          currentUser,
                          "challenge.editProjectAssignees",
                          idea.challenge,
                        )
                      }
                    >
                      {
                        <p>
                          <Icon icon={UserPlusIcon} /> Assign user{" "}
                          {idea?.ownerChallenge?.projectManagementVisibility !==
                          "users" ? (
                            <Popup
                              trigger={
                                <Icon
                                  icon={CircleQuestionMarkIcon}
                                  color="grey"
                                  style={{ marginLeft: 10 }}
                                />
                              }
                            >
                              {`This project board is only visible to ${t("generic.challenge")} administrators. Only
                  assignees that are ${t("generic.challenge")} administrators will be able to view the project
                  board and ${t("generic.ideas")}.`}
                            </Popup>
                          ) : null}
                        </p>
                      }
                    </Button>
                  }
                  onComplete={(users) => {
                    addAllAssignees(
                      users
                        .filter((u) => !board?.assignees?.includes(u._id))
                        .map((u) => u as ChooserUser),
                      assignmentNote,
                    );
                  }}
                  clearOnComplete
                  searchFunction={handleSearch}
                  enabledFeatures={{ search: true, invite: true }}
                  forType="projectInvited"
                  confirm="Assign these users"
                  afterContent={(extraContentProps) => (
                    <div>
                      <ChallengeAudienceWarning
                        extraContentProps={extraContentProps}
                        externalInvitesInAudience={
                          (idea?.ownerChallenge?.visibility?.organisations
                            ?.length ?? 0) > 0 &&
                          idea?.ownerChallenge?.projectManagementVisibility ===
                            "users"
                        }
                      >
                        <ChallengeAudienceWarning.AssigneeAudienceWarning
                          challengeId={challengeId}
                        />
                      </ChallengeAudienceWarning>
                      <div>
                        <H5>
                          Assignment note
                          <Subheader>
                            This note will be added to the &apos;Project
                            Notes&apos; along with information about who has
                            been assigned. Use it to explain why the user(s)
                            have been assigned.
                          </Subheader>
                        </H5>

                        <Input
                          fluid
                          placeholder="Add an assignment note"
                          value={assignmentNote}
                          onChange={(_e, { value }) => setAssignmentNote(value)}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div>
        {userIsAssigned ||
        canManage ||
        util.hasPermission(
          currentUser,
          "challenge.viewProjectNotes",
          idea.challenge,
        ) ? (
          <ProjectComments
            canManage={canManage}
            forId={challengeId}
            forIdea={ideaId}
            canPost={userIsAssigned || canManage}
          />
        ) : null}
      </div>
      <Modal
        open={invitationsModalOpen}
        onClose={() => setInvitationsModalOpen(false)}
        style={{ width: "min(700px, 100%)" }}
      >
        <ModalHeader>Pending invitations</ModalHeader>
        <PendingInvites>
          {pendingInvitations.length > 0 &&
            pendingInvitations.map((i) =>
              i?.invitee ? (
                <PendingInvite key={i._id}>
                  <p style={{ display: "inline-block" }}>{i.invitee}</p>
                  <div>
                    <span>
                      Invited by{" "}
                      <b>
                        {i.inviterUser
                          ? util.getProfileName(i.inviterUser)
                          : "someone"}
                      </b>{" "}
                      on {formatDate(i.createdAt, "dd/MM/yy")}
                    </span>
                    {canManage ||
                    util.hasPermission(
                      currentUser,
                      "challenge.editProjectAssignees",
                      challengeId,
                    ) ? (
                      <Button
                        size="small"
                        style={{ marginLeft: 20 }}
                        onClick={() => removePendingInvitation(i._id)}
                        variant="outline"
                      >
                        <Icon icon={Trash2Icon} />
                      </Button>
                    ) : null}
                  </div>
                </PendingInvite>
              ) : null,
            )}
        </PendingInvites>
        <ModalActions>
          <Button onClick={() => setInvitationsModalOpen(false)}>Close</Button>
        </ModalActions>
      </Modal>
    </>
  );
};
